import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, FileDown, Home as HomeIcon, Loader2, LogOut, Megaphone, MessageCircle, Package, Pencil, Plus, ShoppingCart, Sparkles, Star, Store, Trash2, Truck, Wallet } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { fcfa, PAYMENT_LABELS, PAYMENT_STATUS_LABELS, STATUS_LABELS } from "@/lib/format";
import { BASE_CATEGORIES } from "@/lib/categories";
import ProductForm from "@/components/admin/ProductForm";
import HomeSettings from "@/components/admin/HomeSettings";
import AdminReviews from "@/components/admin/AdminReviews";
import AdminAssistant from "@/components/admin/AdminAssistant";
import AdminAccount from "@/components/admin/AdminAccount";

const ORDER_STATUSES = ["en_attente", "confirmee", "en_preparation", "en_livraison", "livree", "annulee"];
const PAY_STATUSES = ["en_attente", "acompte_recu", "payee", "echouee"];
const CATEGORIES = BASE_CATEGORIES;
const DELIVERY_CITIES = ["Dakar", "Thiès", "Mbour", "Saint-Louis"];

const inputClass =
  "h-11 w-full rounded-md border border-clay bg-white px-3 text-sm text-bark placeholder:text-bark/35 focus:border-terracotta focus:outline-none transition-colors duration-300";

const AdminDashboard = () => {
  useSeo({ title: "Tableau de bord", noindex: true });
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const location = useLocation();
  const [tab, setTab] = useState(location.state?.tab || "orders");
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportCat, setExportCat] = useState("");
  const [saving, setSaving] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: "", category: CATEGORIES[0], price: "", image: "", description: "", badge: "", delivery_estimate: "" });
  const [options, setOptions] = useState([]);
  const [optForm, setOptForm] = useState({ name: "", delay: "", price: "", city: "Dakar" });
  const [editingOptId, setEditingOptId] = useState(null);
  const [savingOpt, setSavingOpt] = useState(false);
  const [driverForms, setDriverForms] = useState({});
  const [openAdminCats, setOpenAdminCats] = useState(new Set(["Robinets"]));
  const [dbCats, setDbCats] = useState(BASE_CATEGORIES);
  const [newCat, setNewCat] = useState("");
  const [savingCat, setSavingCat] = useState(false);
  const [confirmDeleteCat, setConfirmDeleteCat] = useState(null);
  const [renameCat, setRenameCat] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [savingRename, setSavingRename] = useState(false);

  const renameCategory = async (e) => {
    e.preventDefault();
    const newName = renameValue.trim();
    if (!newName || !renameCat) return;
    setSavingRename(true);
    try {
      const { data } = await api.put(`/admin/categories/${encodeURIComponent(renameCat)}`, { new_name: newName });
      setDbCats((prev) => prev.map((c) => (c === renameCat ? data.name : c)));
      setProducts((prev) => prev.map((p) => (p.category === renameCat ? { ...p, category: data.name } : p)));
      refreshCatalogData();
      toast.success(`Catégorie renommée en « ${data.name} »`);
      setRenameCat(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingRename(false);
    }
  };

  const toggleAdminCat = (cat) =>
    setOpenAdminCats((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });

  const catSlug = (s) => s.toLowerCase().replace(/\s/g, "-").normalize("NFD").replace(/[̀-ͯ]/g, "");

  const extraCats = [...new Set(products.map((p) => p.category))].filter((c) => c && !dbCats.includes(c)).sort();
  const adminCats = [...dbCats, ...extraCats];

  const [banner, setBanner] = useState(null);
  const [savingBanner, setSavingBanner] = useState(false);
  const [shopForm, setShopForm] = useState(null);
  const [savingShop, setSavingShop] = useState(false);

  const saveShop = async () => {
    setSavingShop(true);
    try {
      const { data } = await api.put("/admin/settings/shop", shopForm);
      setShopForm(data);
      localStorage.setItem("sk_shop", JSON.stringify(data));
      toast.success("Paramètres de la boutique enregistrés — visibles sur le site");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingShop(false);
    }
  };

  const saveBanner = async () => {
    setSavingBanner(true);
    try {
      const { data } = await api.put("/admin/settings/banner", banner);
      setBanner(data);
      localStorage.setItem("sk_banner", JSON.stringify(data));
      toast.success("Bandeaux enregistrés — visibles sur la boutique au prochain chargement");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingBanner(false);
    }
  };

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        api.get("/admin/orders").then((res) => setOrders(res.data));
        api.get("/products").then((res) => setProducts(res.data));
        api.get("/categories").then((res) => {
          if (res.data?.length) setDbCats(res.data.map((c) => c.name));
        });
        api.get("/delivery-options").then((res) => setOptions(res.data));
        api.get("/settings/banner").then((res) => setBanner(res.data));
        api.get("/settings/shop").then((res) => setShopForm(res.data));
      })
      .catch(() => navigate("/admin/login"));
  }, [navigate]);

  // Rafraîchissement automatique des commandes toutes les 15 s sur l'onglet Commandes
  useEffect(() => {
    if (!user || tab !== "orders") return;
    const refresh = () => api.get("/admin/orders").then((res) => setOrders(res.data)).catch(() => {});
    const interval = setInterval(refresh, 15000);
    return () => clearInterval(interval);
  }, [user, tab]);

  const saveOption = async (e) => {
    e.preventDefault();
    setSavingOpt(true);
    try {
      const payload = { name: optForm.name, delay: optForm.delay, price: parseInt(optForm.price, 10), city: optForm.city };
      if (editingOptId) {
        const { data } = await api.put(`/admin/delivery-options/${editingOptId}`, payload);
        setOptions((prev) => prev.map((o) => (o.id === editingOptId ? data : o)));
        toast.success("Option de livraison modifiée");
      } else {
        const { data } = await api.post("/admin/delivery-options", payload);
        setOptions((prev) => [...prev, data].sort((a, b) => a.price - b.price));
        toast.success("Option de livraison ajoutée");
      }
      setOptForm({ name: "", delay: "", price: "", city: "Dakar" });
      setEditingOptId(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingOpt(false);
    }
  };

  const deleteOption = async (id) => {
    try {
      await api.delete(`/admin/delivery-options/${id}`);
      setOptions((prev) => prev.filter((o) => o.id !== id));
      toast.success("Option de livraison supprimée");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setDriverField = (orderId, key, value) =>
    setDriverForms((prev) => ({ ...prev, [orderId]: { ...(prev[orderId] || {}), [key]: value } }));

  const saveDriver = async (order) => {
    const name = (driverForms[order.id]?.name ?? order.driver?.name ?? "").trim();
    const phone = (driverForms[order.id]?.phone ?? order.driver?.phone ?? "").trim();
    if (!name || !phone) {
      toast.error("Nom et téléphone du livreur requis");
      return;
    }
    try {
      const { data } = await api.patch(`/admin/orders/${order.id}`, { driver: { name, phone } });
      setOrders((prev) => prev.map((o) => (o.id === order.id ? data : o)));
      toast.success("Livreur enregistré — visible par le client dans le suivi");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveShippingFee = async (p, value) => {
    const fee = parseInt(value, 10);
    if (isNaN(fee) || fee < 0 || fee === (p.shipping_fee ?? 0)) return;
    try {
      const { data } = await api.patch(`/admin/products/${p.id}`, { shipping_fee: fee });
      setProducts((prev) => prev.map((x) => (x.id === p.id ? data : x)));
      toast.success(`Frais de livraison de « ${p.name} » mis à jour`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  const updateOrder = async (id, field, value) => {
    try {
      const { data } = await api.patch(`/admin/orders/${id}`, { [field]: value });
      setOrders((prev) => prev.map((o) => (o.id === id ? data : o)));
      toast.success(field === "status" && value === "en_livraison" ? "Commande en livraison — SMS envoyé au client" : "Commande mise à jour");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteProduct = async (id) => {
    try {
      await api.delete(`/admin/products/${id}`);
      setProducts((prev) => prev.filter((p) => p.id !== id));
      setConfirmDelete(null);
      toast.success("Produit supprimé définitivement");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmDeleteOrder, setConfirmDeleteOrder] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const [feeInputs, setFeeInputs] = useState({});
  const [discountInputs, setDiscountInputs] = useState({});

  const applyDiscount = async (order) => {
    const pct = parseInt(discountInputs[order.id], 10);
    if (Number.isNaN(pct)) {
      toast.error("Choisissez un pourcentage de réduction");
      return;
    }
    try {
      const { data } = await api.patch(`/admin/orders/${order.id}`, { discount_percent: pct });
      setOrders((prev) => prev.map((o) => (o.id === order.id ? data : o)));
      toast.success(`Réduction −${pct}% appliquée — nouveau total ${fcfa(data.total)}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeDiscount = async (order) => {
    try {
      const { data } = await api.patch(`/admin/orders/${order.id}`, { discount_percent: 0 });
      setOrders((prev) => prev.map((o) => (o.id === order.id ? data : o)));
      toast.success("Réduction retirée");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveDeliveryFee = async (order) => {
    const fee = parseInt(feeInputs[order.id], 10);
    if (Number.isNaN(fee) || fee < 0) {
      toast.error("Montant invalide");
      return;
    }
    try {
      const { data } = await api.patch(`/admin/orders/${order.id}`, { delivery_fee: fee });
      setOrders((prev) => prev.map((o) => (o.id === order.id ? data : o)));
      toast.success(`Frais de livraison définis : ${fcfa(fee)} — nouveau total ${fcfa(data.total)}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteOrder = async (order) => {
    try {
      await api.delete(`/admin/orders/${order.id}`);
      setOrders((prev) => prev.filter((o) => o.id !== order.id));
      setConfirmDeleteOrder(null);
      toast.success(`Commande ${order.order_number} supprimée`);
    } catch (err) {
      setConfirmDeleteOrder(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const refreshCatalogData = () => {
    api.get("/categories").then((res) => {
      if (res.data?.length) setDbCats(res.data.map((c) => c.name));
    }).catch(() => {});
    api.get("/products").then((res) => setProducts(res.data)).catch(() => {});
  };

  const createCategory = async (e) => {
    e.preventDefault();
    const name = newCat.trim();
    if (!name) return;
    setSavingCat(true);
    try {
      const { data } = await api.post("/admin/categories", { name });
      setDbCats((prev) => [...prev, data.name]);
      setNewCat("");
      refreshCatalogData();
      toast.success(`Catégorie « ${data.name} » créée`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingCat(false);
    }
  };

  const deleteCategory = async (name) => {
    try {
      await api.delete(`/admin/categories/${encodeURIComponent(name)}`);
      setDbCats((prev) => prev.filter((c) => c !== name));
      setConfirmDeleteCat(null);
      refreshCatalogData();
      toast.success(`Catégorie « ${name} » supprimée`);
    } catch (err) {
      setConfirmDeleteCat(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const addProduct = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/admin/products", {
        name: newProduct.name,
        category: newProduct.category,
        price: parseInt(newProduct.price, 10),
        image: newProduct.image,
        description: newProduct.description,
        badge: newProduct.badge || null,
        delivery_estimate: newProduct.delivery_estimate,
        features: [],
      });
      setProducts((prev) => [...prev, data]);
      setNewProduct({ name: "", category: CATEGORIES[0], price: "", image: "", description: "", badge: "", delivery_estimate: "" });
      setShowForm(false);
      toast.success("Produit ajouté");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const exportCatalog = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/catalog.pdf", {
        params: exportCat ? { category: exportCat } : {},
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = exportCat
        ? `catalogue-sama-keur-${exportCat.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`
        : "catalogue-sama-keur.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success(exportCat ? `Catalogue « ${exportCat} » téléchargé` : "Catalogue PDF téléchargé");
    } catch (err) {
      let detail = err.response?.data;
      if (detail instanceof Blob) {
        try {
          detail = JSON.parse(await detail.text())?.detail;
        } catch {
          detail = null;
        }
      } else {
        detail = detail?.detail;
      }
      toast.error(formatApiErrorDetail(detail) || "Export impossible");
    } finally {
      setExporting(false);
    }
  };

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-sand">
        <Loader2 className="h-8 w-8 animate-spin text-terracotta" />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="flex min-h-screen bg-sand">
      <aside className="flex w-60 shrink-0 flex-col border-r border-clay bg-cream p-6">
        <Link to="/" className="font-display text-2xl font-bold text-bark">
          Sama-<span className="italic text-terracotta">Keur</span>
        </Link>
        <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-bark/40">Administration</p>
        <nav className="mt-10 flex flex-col gap-2">
          <button
            data-testid="admin-tab-orders"
            onClick={() => setTab("orders")}
            className={`flex items-center gap-3 rounded-md px-4 py-3 text-sm font-semibold transition-colors duration-300 ${tab === "orders" ? "bg-terracotta text-sand" : "text-bark/60 hover:bg-clay/50"}`}
          >
            <ShoppingCart className="h-4 w-4" /> Commandes
          </button>
          <button
            data-testid="admin-tab-products"
            onClick={() => setTab("products")}
            className={`flex items-center gap-3 rounded-md px-4 py-3 text-sm font-semibold transition-colors duration-300 ${tab === "products" ? "bg-terracotta text-sand" : "text-bark/60 hover:bg-clay/50"}`}
          >
            <Package className="h-4 w-4" /> Produits
          </button>
          <button
            data-testid="admin-tab-delivery"
            onClick={() => setTab("delivery")}
            className={`flex items-center gap-3 rounded-md px-4 py-3 text-sm font-semibold transition-colors duration-300 ${tab === "delivery" ? "bg-terracotta text-sand" : "text-bark/60 hover:bg-clay/50"}`}
          >
            <Truck className="h-4 w-4" /> Frais de livraison
          </button>
          <button
            data-testid="admin-tab-banner"
            onClick={() => setTab("banner")}
            className={`flex items-center gap-3 rounded-md px-4 py-3 text-sm font-semibold transition-colors duration-300 ${tab === "banner" ? "bg-terracotta text-sand" : "text-bark/60 hover:bg-clay/50"}`}
          >
            <Megaphone className="h-4 w-4" /> Bandeaux
          </button>
          <button
            data-testid="admin-tab-home"
            onClick={() => setTab("home")}
            className={`flex items-center gap-3 rounded-md px-4 py-3 text-sm font-semibold transition-colors duration-300 ${tab === "home" ? "bg-terracotta text-sand" : "text-bark/60 hover:bg-clay/50"}`}
          >
            <HomeIcon className="h-4 w-4" /> Accueil
          </button>
          <button
            data-testid="admin-tab-payments"
            onClick={() => setTab("payments")}
            className={`flex items-center gap-3 rounded-md px-4 py-3 text-sm font-semibold transition-colors duration-300 ${tab === "payments" ? "bg-terracotta text-sand" : "text-bark/60 hover:bg-clay/50"}`}
          >
            <Wallet className="h-4 w-4" /> Paiements
          </button>
          <button
            data-testid="admin-tab-reviews"
            onClick={() => setTab("reviews")}
            className={`flex items-center gap-3 rounded-md px-4 py-3 text-sm font-semibold transition-colors duration-300 ${tab === "reviews" ? "bg-terracotta text-sand" : "text-bark/60 hover:bg-clay/50"}`}
          >
            <Star className="h-4 w-4" /> Avis
          </button>
          <button
            data-testid="admin-tab-assistant"
            onClick={() => setTab("assistant")}
            className={`flex items-center gap-3 rounded-md px-4 py-3 text-sm font-semibold transition-colors duration-300 ${tab === "assistant" ? "bg-terracotta text-sand" : "text-bark/60 hover:bg-clay/50"}`}
          >
            <Sparkles className="h-4 w-4" /> Assistant IA
          </button>
          <button
            data-testid="admin-tab-shop"
            onClick={() => setTab("shop")}
            className={`flex items-center gap-3 rounded-md px-4 py-3 text-sm font-semibold transition-colors duration-300 ${tab === "shop" ? "bg-terracotta text-sand" : "text-bark/60 hover:bg-clay/50"}`}
          >
            <Store className="h-4 w-4" /> Paramètres boutique
          </button>
        </nav>
        <div className="mt-auto">
          <p className="mb-3 truncate text-xs text-bark/40">{user.email}</p>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="flex w-full items-center gap-3 rounded-md border border-clay px-4 py-3 text-sm font-semibold text-bark/60 transition-colors hover:border-terracotta hover:text-terracotta"
          >
            <LogOut className="h-4 w-4" /> Déconnexion
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-auto p-8 lg:p-12">
        {tab === "orders" && (
          <section>
            <h1 className="font-display text-3xl font-semibold text-bark">Commandes</h1>
            <p className="mt-1 text-sm text-bark/50">
              {orders.length} commande{orders.length > 1 ? "s" : ""} au total. Quand une commande passe « En livraison », un SMS est envoyé au client
              (simulé tant que Twilio n'est pas configuré) et le livreur renseigné apparaît dans son suivi.
            </p>
            <div className="mt-8 overflow-x-auto border border-clay bg-cream">
              <table className="w-full min-w-[1100px] text-left text-sm">
                <thead>
                  <tr className="border-b border-clay text-[10px] font-bold uppercase tracking-[0.2em] text-bark/40">
                    <th className="px-5 py-4">N°</th>
                    <th className="px-5 py-4">Client</th>
                    <th className="px-5 py-4">Articles</th>
                    <th className="px-5 py-4">Total</th>
                    <th className="px-5 py-4">Paiement</th>
                    <th className="px-5 py-4">Statut</th>
                    <th className="px-5 py-4">Livreur</th>
                    <th className="px-5 py-4" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-clay">
                  {orders.length === 0 && (
                    <tr>
                      <td colSpan={8} data-testid="admin-orders-empty" className="px-5 py-12 text-center text-bark/40">
                        Aucune commande pour le moment.
                      </td>
                    </tr>
                  )}
                  {orders.map((o) => (
                    <tr key={o.id} data-testid={`admin-order-row-${o.order_number}`} className="align-top">
                      <td className="px-5 py-4">
                        <p className="font-bold text-terracotta">{o.order_number}</p>
                        <p className="mt-1 text-xs text-bark/40">{new Date(o.created_at).toLocaleDateString("fr-FR")}</p>
                      </td>
                      <td className="px-5 py-4">
                        <p className="font-semibold text-bark">{o.customer.name}</p>
                        <p className="text-xs text-bark/50">{o.customer.phone}</p>
                        <p className="mt-1 max-w-[220px] text-xs text-bark/40">
                          {o.delivery_zone} — {o.customer.address}
                        </p>
                        {o.delivery_fee_pending && (
                          <div data-testid={`admin-fee-pending-${o.order_number}`} className="mt-2 border-t border-clay pt-2">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-ochre">
                              Autre ville : {o.delivery_zone} — frais à définir
                            </p>
                            <div className="mt-1.5 flex items-center gap-1.5">
                              <input
                                type="number"
                                min="0"
                                placeholder="Frais FCFA"
                                value={feeInputs[o.id] ?? ""}
                                onChange={(e) => setFeeInputs((prev) => ({ ...prev, [o.id]: e.target.value }))}
                                data-testid={`admin-fee-input-${o.order_number}`}
                                className="h-8 w-24 rounded border border-clay bg-white px-2 text-xs focus:border-terracotta focus:outline-none"
                              />
                              <button
                                data-testid={`admin-fee-save-${o.order_number}`}
                                onClick={() => saveDeliveryFee(o)}
                                className="h-8 rounded-full bg-terracotta px-3 text-[10px] font-bold uppercase tracking-widest text-sand transition-colors hover:bg-terracotta-dark"
                              >
                                Définir
                              </button>
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-4 text-xs text-bark/60">
                        {o.items.map((i, idx) => {
                          const prod = products.find((p) => p.id === i.product_id);
                          return (
                            <p key={idx}>
                              <button
                                type="button"
                                data-testid={`order-item-link-${o.order_number}-${idx}`}
                                onClick={() => setPreviewItem({ item: i, product: prod || null })}
                                className="text-left font-medium text-bark underline-offset-2 transition-colors hover:text-terracotta hover:underline"
                              >
                                {i.name}
                              </button>{" "}
                              ×{i.quantity}
                            </p>
                          );
                        })}
                      </td>
                      <td className="px-5 py-4 font-bold text-bark">
                        {o.discount_percent > 0 && (
                          <p className="text-xs font-normal text-bark/40 line-through">{fcfa(o.total + o.discount_amount)}</p>
                        )}
                        {fcfa(o.total)}
                        {o.discount_percent > 0 && (
                          <span data-testid={`admin-discount-badge-${o.order_number}`} className="ml-2 rounded-full bg-terracotta/10 px-2 py-0.5 text-[10px] font-bold text-terracotta">
                            −{o.discount_percent}%
                          </span>
                        )}
                        {!["livree", "annulee"].includes(o.status) && (
                          <div className="mt-2 flex items-center gap-1.5 font-normal">
                            <select
                              data-testid={`admin-discount-select-${o.order_number}`}
                              value={discountInputs[o.id] ?? o.discount_percent ?? ""}
                              onChange={(e) => setDiscountInputs((prev) => ({ ...prev, [o.id]: e.target.value }))}
                              className="h-8 rounded border border-clay bg-white px-1.5 text-xs focus:border-terracotta focus:outline-none"
                            >
                              <option value="">Réduction…</option>
                              {[5, 10, 15, 20, 25, 30, 35, 40, 45, 50].map((p) => (
                                <option key={p} value={p}>{`−${p}%`}</option>
                              ))}
                            </select>
                            <button
                              data-testid={`admin-discount-apply-${o.order_number}`}
                              onClick={() => applyDiscount(o)}
                              className="h-8 rounded-full bg-bark px-3 text-[10px] font-bold uppercase tracking-widest text-sand transition-colors hover:bg-terracotta"
                            >
                              Appliquer
                            </button>
                            {o.discount_percent > 0 && (
                              <button
                                data-testid={`admin-discount-remove-${o.order_number}`}
                                onClick={() => removeDiscount(o)}
                                className="h-8 rounded-full border border-clay px-2.5 text-[10px] font-bold uppercase text-bark/50 transition-colors hover:border-destructive hover:text-destructive"
                              >
                                Retirer
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-xs font-semibold text-bark">{PAYMENT_LABELS[o.payment_method]}</p>
                        <select
                          data-testid={`admin-payment-status-${o.order_number}`}
                          value={o.payment_status}
                          onChange={(e) => updateOrder(o.id, "payment_status", e.target.value)}
                          className="mt-1 h-8 rounded border border-clay bg-white px-2 text-xs focus:border-terracotta focus:outline-none"
                        >
                          {PAY_STATUSES.map((s) => (
                            <option key={s} value={s}>{PAYMENT_STATUS_LABELS[s]}</option>
                          ))}
                        </select>
                        {o.client_payment_confirmed && o.payment_status !== "payee" && (
                          <p data-testid={`client-confirmed-badge-${o.order_number}`} className="mt-1 text-[10px] font-bold uppercase tracking-widest text-terracotta">
                            Client : paiement envoyé
                          </p>
                        )}
                        {o.payment_plan === "two_times" && (
                          <div data-testid={`payment-plan-detail-${o.order_number}`} className="mt-2 space-y-0.5 border-t border-clay pt-2 text-[11px] leading-snug">
                            <p className="text-bark/60">
                              1er versement : <strong className="text-bark">{fcfa(o.deposit_amount)}</strong>
                              {o.payment_status === "en_attente" ? " (en attente)" : " (reçu)"}
                            </p>
                            <p className="font-semibold text-terracotta">
                              Reste à la livraison : {fcfa(o.remaining_amount)}
                            </p>
                            {o.payment_status !== "payee" && (
                              <a
                                data-testid={`balance-reminder-${o.order_number}`}
                                href={`https://wa.me/${(o.customer.phone || "").replace(/\D/g, "")}?text=${encodeURIComponent(
                                  `Bonjour ${o.customer.name}, votre commande ${o.order_number} sera bientôt livrée. Il restera ${fcfa(o.remaining_amount)} à régler à la livraison. Merci ! — Sama-Keur`
                                )}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-1 inline-flex items-center gap-1.5 rounded-full border border-terracotta px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-terracotta transition-colors hover:bg-terracotta hover:text-sand"
                              >
                                <MessageCircle className="h-3.5 w-3.5" /> Relancer le solde
                              </a>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <select
                          data-testid={`admin-order-status-${o.order_number}`}
                          value={o.status}
                          onChange={(e) => updateOrder(o.id, "status", e.target.value)}
                          className="h-8 rounded border border-clay bg-white px-2 text-xs focus:border-terracotta focus:outline-none"
                        >
                          {ORDER_STATUSES.map((s) => (
                            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-5 py-4">
                        <input
                          data-testid={`admin-driver-name-${o.order_number}`}
                          placeholder="Nom du livreur"
                          value={driverForms[o.id]?.name ?? o.driver?.name ?? ""}
                          onChange={(e) => setDriverField(o.id, "name", e.target.value)}
                          className="mb-1 block h-8 w-36 rounded border border-clay bg-white px-2 text-xs focus:border-terracotta focus:outline-none"
                        />
                        <input
                          data-testid={`admin-driver-phone-${o.order_number}`}
                          placeholder="Téléphone"
                          value={driverForms[o.id]?.phone ?? o.driver?.phone ?? ""}
                          onChange={(e) => setDriverField(o.id, "phone", e.target.value)}
                          className="mb-1 block h-8 w-36 rounded border border-clay bg-white px-2 text-xs focus:border-terracotta focus:outline-none"
                        />
                        <button
                          data-testid={`admin-driver-save-${o.order_number}`}
                          onClick={() => saveDriver(o)}
                          className="h-7 rounded-full bg-terracotta px-3 text-[10px] font-bold uppercase tracking-widest text-sand transition-colors hover:bg-terracotta-dark"
                        >
                          Enregistrer
                        </button>
                      </td>
                      <td className="px-5 py-4">
                        <button
                          data-testid={`admin-order-delete-${o.order_number}`}
                          onClick={() => setConfirmDeleteOrder(o)}
                          className="flex h-8 w-8 items-center justify-center rounded-full border border-clay text-bark/40 transition-colors hover:border-destructive hover:text-destructive"
                          aria-label={`Supprimer la commande ${o.order_number}`}
                          title="Supprimer cette commande"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tab === "products" && (
          <section>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="font-display text-3xl font-semibold text-bark">Produits</h1>
                <p className="mt-1 text-sm text-bark/50">{products.length} produit{products.length > 1 ? "s" : ""} au catalogue.</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  data-testid="admin-add-product-toggle"
                  onClick={() => setShowForm((v) => !v)}
                  className="flex h-11 items-center gap-2 rounded-full bg-terracotta px-6 text-xs font-bold uppercase tracking-[0.15em] text-sand transition-colors hover:bg-terracotta-dark"
                >
                  <Plus className="h-4 w-4" /> Ajouter
                </button>
              </div>
            </div>

            <div data-testid="admin-catalog-pdf-section" className="mt-6 flex flex-wrap items-center gap-3 border border-clay bg-cream p-5">
              <div className="mr-auto">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-terracotta">Catalogue PDF</p>
                <p className="mt-1 text-xs text-bark/50">
                  Tout le catalogue en tableau compact, ou une catégorie en fiches détaillées (grande photo, description, caractéristiques).
                </p>
              </div>
              <select
                data-testid="admin-export-category-select"
                value={exportCat}
                onChange={(e) => setExportCat(e.target.value)}
                className="h-11 rounded-md border border-clay bg-white px-3 text-sm text-bark focus:border-terracotta focus:outline-none"
              >
                <option value="">Tout le catalogue</option>
                {adminCats.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <button
                data-testid="admin-export-catalog-button"
                onClick={exportCatalog}
                disabled={exporting}
                className="flex h-11 items-center gap-2 rounded-full border border-bark px-5 text-xs font-bold uppercase tracking-[0.15em] text-bark transition-colors hover:border-terracotta hover:text-terracotta disabled:opacity-50"
              >
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                {exporting ? "Génération…" : "Générer le PDF"}
              </button>
            </div>

            {showForm && (
              <ProductForm
                categories={adminCats}
                onCreated={(p) => {
                  setProducts((prev) => [...prev, p]);
                  setShowForm(false);
                }}
              />
            )}

            <form onSubmit={createCategory} data-testid="admin-category-form" className="mt-6 flex flex-col gap-3 border border-clay bg-cream p-4 sm:flex-row sm:items-center">
              <input
                data-testid="admin-category-name-input"
                value={newCat}
                onChange={(e) => setNewCat(e.target.value)}
                placeholder="Nouvelle catégorie (ex : Cuisine)"
                className={`${inputClass} sm:max-w-xs`}
              />
              <button
                type="submit"
                disabled={savingCat || !newCat.trim()}
                data-testid="admin-category-create-button"
                className="flex h-11 shrink-0 items-center justify-center gap-2 rounded-full border border-terracotta px-6 text-xs font-bold uppercase tracking-[0.15em] text-terracotta transition-colors hover:bg-terracotta hover:text-sand disabled:opacity-50"
              >
                {savingCat && <Loader2 className="h-4 w-4 animate-spin" />}
                Créer la catégorie
              </button>
            </form>

            <div className="mt-8 space-y-3">
              {adminCats.map((cat) => {
                const catProducts = products.filter((p) => p.category === cat);
                const open = openAdminCats.has(cat);
                return (
                  <div key={cat} data-testid={`admin-cat-group-${catSlug(cat)}`} className="border border-clay bg-cream">
                    <div className="flex items-center">
                      <button
                        data-testid={`admin-cat-toggle-${catSlug(cat)}`}
                        onClick={() => toggleAdminCat(cat)}
                        className="flex flex-1 items-center justify-between px-5 py-4 text-left"
                      >
                        <div className="flex items-center gap-3">
                          <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-180 text-terracotta" : "text-bark/40"}`} />
                          <span className="font-display text-xl font-semibold text-bark">{cat}</span>
                          <span className="rounded-full bg-clay/60 px-2.5 py-0.5 text-[10px] font-bold text-bark/60">{catProducts.length}</span>
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-bark/35">{open ? "Replier" : "Ouvrir"}</span>
                      </button>
                      <button
                        data-testid={`admin-cat-rename-${catSlug(cat)}`}
                        onClick={() => {
                          setRenameCat(cat);
                          setRenameValue(cat);
                        }}
                        className="mr-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-clay text-bark/40 transition-colors hover:border-terracotta hover:text-terracotta"
                        aria-label={`Renommer la catégorie ${cat}`}
                        title="Renommer cette catégorie"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      {catProducts.length === 0 && (
                        <button
                          data-testid={`admin-cat-delete-${catSlug(cat)}`}
                          onClick={() => setConfirmDeleteCat(cat)}
                          className="mr-4 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-clay text-bark/40 transition-colors hover:border-destructive hover:text-destructive"
                          aria-label={`Supprimer la catégorie ${cat}`}
                          title="Supprimer cette catégorie vide"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    {open && (
                      <div className="overflow-x-auto border-t border-clay">
                        {catProducts.length === 0 ? (
                          <p className="px-5 py-8 text-center text-sm text-bark/40">Aucun produit dans cette catégorie.</p>
                        ) : (
                          <table className="w-full min-w-[750px] text-left text-sm">
                            <thead>
                              <tr className="border-b border-clay bg-sand/50 text-[10px] font-bold uppercase tracking-[0.2em] text-bark/40">
                                <th className="px-5 py-3">Produit</th>
                                <th className="px-5 py-3">Prix</th>
                                <th className="px-5 py-3">Frais de livraison France</th>
                                <th className="px-5 py-3"></th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-clay">
                              {catProducts.map((p) => (
                                <tr key={p.id} data-testid={`admin-product-row-${p.id}`}>
                                  <td className="px-5 py-3">
                                    <Link to={`/admin/produits/${p.id}`} data-testid={`admin-product-edit-link-${p.id}`} className="group flex items-center gap-3" title="Voir / modifier le produit">
                                      <div className="h-12 w-10 shrink-0 overflow-hidden border border-clay">
                                        <img src={p.image} alt={p.name} className="h-full w-full object-cover" />
                                      </div>
                                      <p className="font-semibold text-bark transition-colors group-hover:text-terracotta group-hover:underline">{p.name}</p>
                                    </Link>
                                  </td>
                                  <td className="px-5 py-3 font-bold text-bark">{fcfa(p.price)}</td>
                                  <td className="px-5 py-3">
                                    <input
                                      type="number"
                                      min="0"
                                      key={p.id + (p.shipping_fee ?? 0)}
                                      defaultValue={p.shipping_fee ?? 0}
                                      onBlur={(e) => saveShippingFee(p, e.target.value)}
                                      data-testid={`admin-shipping-fee-${p.id}`}
                                      className="h-8 w-28 rounded border border-clay bg-white px-2 text-xs focus:border-terracotta focus:outline-none"
                                    />
                                    <span className="ml-1 text-xs text-bark/40">FCFA</span>
                                  </td>
                                  <td className="px-5 py-3 text-right">
                                    <button
                                      data-testid={`admin-product-delete-${p.id}`}
                                      onClick={() => setConfirmDelete(p)}
                                      className="text-bark/30 transition-colors hover:text-destructive"
                                      aria-label={`Supprimer ${p.name}`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {tab === "home" && <HomeSettings products={products} />}

        {tab === "reviews" && <AdminReviews />}

        {tab === "assistant" && <AdminAssistant />}

        {tab === "delivery" && (
          <section>
            <h1 className="font-display text-3xl font-semibold text-bark">Frais de livraison</h1>
            <p className="mt-1 text-sm text-bark/50">
              Livraison à domicile uniquement — Dakar, Thiès, Mbour & Saint-Louis. Chaque option est liée à une ville et s'affiche dans le panier et au paiement.
            </p>

            <form onSubmit={saveOption} data-testid="admin-delivery-form" className="mt-6 grid gap-3 border border-clay bg-cream p-6 sm:grid-cols-4">
              <input required placeholder="Nom (ex : Standard) *" value={optForm.name} onChange={(e) => setOptForm((f) => ({ ...f, name: e.target.value }))} className={inputClass} data-testid="admin-delivery-name-input" />
              <input required placeholder="Délai (ex : 2 à 3 jours) *" value={optForm.delay} onChange={(e) => setOptForm((f) => ({ ...f, delay: e.target.value }))} className={inputClass} data-testid="admin-delivery-delay-input" />
              <input required type="number" min="0" placeholder="Prix en FCFA *" value={optForm.price} onChange={(e) => setOptForm((f) => ({ ...f, price: e.target.value }))} className={inputClass} data-testid="admin-delivery-price-input" />
              <select value={optForm.city} onChange={(e) => setOptForm((f) => ({ ...f, city: e.target.value }))} className={inputClass} data-testid="admin-delivery-city-select">
                {DELIVERY_CITIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <div className="flex gap-3 sm:col-span-4">
                <button
                  type="submit"
                  disabled={savingOpt}
                  className="flex h-11 items-center justify-center gap-2 rounded-full bg-terracotta px-8 text-xs font-bold uppercase tracking-[0.15em] text-sand transition-colors hover:bg-terracotta-dark disabled:opacity-50"
                  data-testid="admin-delivery-submit-button"
                >
                  {savingOpt && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editingOptId ? "Enregistrer la modification" : "Ajouter l'option"}
                </button>
                {editingOptId && (
                  <button
                    type="button"
                    data-testid="admin-delivery-cancel-edit"
                    onClick={() => { setEditingOptId(null); setOptForm({ name: "", delay: "", price: "", city: "Dakar" }); }}
                    className="flex h-11 items-center rounded-full border border-clay px-6 text-xs font-bold uppercase tracking-[0.15em] text-bark/60 transition-colors hover:border-terracotta hover:text-terracotta"
                  >
                    Annuler
                  </button>
                )}
              </div>
            </form>

            <div className="mt-8 overflow-x-auto border border-clay bg-cream">
              <table className="w-full min-w-[700px] text-left text-sm">
                <thead>
                  <tr className="border-b border-clay text-[10px] font-bold uppercase tracking-[0.2em] text-bark/40">
                    <th className="px-5 py-4">Option</th>
                    <th className="px-5 py-4">Ville</th>
                    <th className="px-5 py-4">Délai</th>
                    <th className="px-5 py-4">Prix</th>
                    <th className="px-5 py-4"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-clay">
                  {options.length === 0 && (
                    <tr>
                      <td colSpan={5} data-testid="admin-delivery-empty" className="px-5 py-12 text-center text-bark/40">
                        Aucune option de livraison. Créez-en une ci-dessus.
                      </td>
                    </tr>
                  )}
                  {options.map((o) => (
                    <tr key={o.id} data-testid={`admin-delivery-row-${o.id}`} className={editingOptId === o.id ? "bg-terracotta/5" : ""}>
                      <td className="px-5 py-4 font-semibold text-bark">{o.name}</td>
                      <td className="px-5 py-4 text-bark/60">{o.city || "Dakar"}</td>
                      <td className="px-5 py-4 text-bark/60">{o.delay}</td>
                      <td className="px-5 py-4 font-bold text-bark">{fcfa(o.price)}</td>
                      <td className="px-5 py-4 text-right">
                        <button
                          data-testid={`admin-delivery-edit-${o.id}`}
                          onClick={() => { setEditingOptId(o.id); setOptForm({ name: o.name, delay: o.delay, price: String(o.price), city: o.city || "Dakar" }); }}
                          className="mr-3 text-bark/30 transition-colors hover:text-terracotta"
                          aria-label={`Modifier ${o.name}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          data-testid={`admin-delivery-delete-${o.id}`}
                          onClick={() => deleteOption(o.id)}
                          className="text-bark/30 transition-colors hover:text-destructive"
                          aria-label={`Supprimer ${o.name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tab === "banner" && banner && (
          <section>
            <h1 className="font-display text-3xl font-semibold text-bark">Bandeaux</h1>
            <p className="mt-1 text-sm text-bark/50">
              Les bandeaux défilants s'affichent en haut de toutes les pages de la boutique.
            </p>

            <div className="mt-8 max-w-2xl space-y-6">
              <div className="border border-clay bg-cream p-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 className="font-display text-xl font-semibold text-bark">Bandeau principal <span className="text-sm font-normal text-bark/40">(fond ocre)</span></h2>
                  <button
                    type="button"
                    data-testid="admin-banner-main-toggle"
                    onClick={() => setBanner((b) => ({ ...b, main_enabled: !b.main_enabled }))}
                    className={`flex h-10 items-center gap-2 rounded-full border px-5 text-[11px] font-bold uppercase tracking-[0.15em] transition-colors duration-300 ${
                      banner.main_enabled ? "border-terracotta bg-terracotta text-sand" : "border-clay bg-white text-bark/50"
                    }`}
                  >
                    <span className={`h-2 w-2 rounded-full ${banner.main_enabled ? "bg-sand" : "bg-bark/30"}`} />
                    {banner.main_enabled ? "Activé" : "Désactivé"}
                  </button>
                </div>
                <input
                  data-testid="admin-banner-main-message"
                  value={banner.main_message}
                  onChange={(e) => setBanner((b) => ({ ...b, main_message: e.target.value }))}
                  placeholder="Message du bandeau principal"
                  className={`${inputClass} mt-4`}
                />
              </div>

              <div className="border border-clay bg-cream p-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 className="font-display text-xl font-semibold text-bark">Bandeau promo <span className="text-sm font-normal text-bark/40">(fond terracotta)</span></h2>
                  <button
                    type="button"
                    data-testid="admin-banner-promo-toggle"
                    onClick={() => setBanner((b) => ({ ...b, promo_enabled: !b.promo_enabled }))}
                    className={`flex h-10 items-center gap-2 rounded-full border px-5 text-[11px] font-bold uppercase tracking-[0.15em] transition-colors duration-300 ${
                      banner.promo_enabled ? "border-terracotta bg-terracotta text-sand" : "border-clay bg-white text-bark/50"
                    }`}
                  >
                    <span className={`h-2 w-2 rounded-full ${banner.promo_enabled ? "bg-sand" : "bg-bark/30"}`} />
                    {banner.promo_enabled ? "Activé" : "Désactivé"}
                  </button>
                </div>
                <input
                  data-testid="admin-banner-promo-message"
                  value={banner.promo_message}
                  onChange={(e) => setBanner((b) => ({ ...b, promo_message: e.target.value }))}
                  placeholder="Ex : -10% ce week-end sur toute la boutique"
                  className={`${inputClass} mt-4`}
                />
                <p className="mt-2 text-xs text-bark/40">Idéal pour une offre ponctuelle : activez-le le temps de la promo, désactivez-le ensuite.</p>
              </div>

              <button
                data-testid="admin-banner-save"
                onClick={saveBanner}
                disabled={savingBanner}
                className="flex h-12 items-center gap-2 rounded-full bg-terracotta px-8 text-xs font-bold uppercase tracking-[0.15em] text-sand transition-colors hover:bg-terracotta-dark disabled:opacity-50"
              >
                {savingBanner && <Loader2 className="h-4 w-4 animate-spin" />}
                Enregistrer les bandeaux
              </button>
            </div>
          </section>
        )}
        {tab === "payments" && shopForm && (
          <section>
            <h1 className="font-display text-3xl font-semibold text-bark">Paramètres de paiement</h1>
            <p className="mt-1 text-sm text-bark/50">
              Les numéros vers lesquels vos clients envoient le paiement. Ils sont affichés au moment de la commande, quand le client choisit Wave ou Orange Money.
            </p>

            <div className="mt-8 max-w-2xl space-y-4 border border-clay bg-cream p-6">
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">Numéro Wave</label>
                <input data-testid="admin-pay-wave-input" value={shopForm.wave_number || ""} onChange={(e) => setShopForm((f) => ({ ...f, wave_number: e.target.value }))} className={inputClass} placeholder="+221 77 123 45 67" />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">Numéro Orange Money</label>
                <input data-testid="admin-pay-om-input" value={shopForm.orange_money_number || ""} onChange={(e) => setShopForm((f) => ({ ...f, orange_money_number: e.target.value }))} className={inputClass} placeholder="+221 77 123 45 67" />
              </div>
              <p className="text-xs text-bark/40">Laissez vide pour ne pas afficher de numéro au client. La carte bancaire (Stripe) ne nécessite aucun numéro.</p>
              <button
                data-testid="admin-pay-save"
                onClick={saveShop}
                disabled={savingShop}
                className="flex h-12 items-center gap-2 rounded-full bg-terracotta px-8 text-xs font-bold uppercase tracking-[0.15em] text-sand transition-colors hover:bg-terracotta-dark disabled:opacity-50"
              >
                {savingShop && <Loader2 className="h-4 w-4 animate-spin" />}
                Enregistrer les paramètres de paiement
              </button>
            </div>
          </section>
        )}
        {tab === "shop" && shopForm && (
          <section>
            <h1 className="font-display text-3xl font-semibold text-bark">Paramètres de la boutique</h1>
            <p className="mt-1 text-sm text-bark/50">
              Ces informations de contact s'affichent dans le pied de page et sur les pages de la boutique.
            </p>

            <div className="mt-8 max-w-2xl space-y-4 border border-clay bg-cream p-6">
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">Téléphone</label>
                <input data-testid="admin-shop-phone-input" value={shopForm.phone} onChange={(e) => setShopForm((f) => ({ ...f, phone: e.target.value }))} className={inputClass} placeholder="+221 77 123 45 67" />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">WhatsApp</label>
                <input data-testid="admin-shop-whatsapp-input" value={shopForm.whatsapp} onChange={(e) => setShopForm((f) => ({ ...f, whatsapp: e.target.value }))} className={inputClass} placeholder="+221 77 123 45 67" />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">Email de contact</label>
                <input data-testid="admin-shop-email-input" type="email" value={shopForm.email} onChange={(e) => setShopForm((f) => ({ ...f, email: e.target.value }))} className={inputClass} placeholder="bonjour@sama-keur.sn" />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">Adresse</label>
                <input data-testid="admin-shop-address-input" value={shopForm.address} onChange={(e) => setShopForm((f) => ({ ...f, address: e.target.value }))} className={inputClass} placeholder="Avenue Cheikh Anta Diop, Dakar, Sénégal" />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">Horaires d'ouverture</label>
                <input data-testid="admin-shop-hours-input" value={shopForm.hours} onChange={(e) => setShopForm((f) => ({ ...f, hours: e.target.value }))} className={inputClass} placeholder="Lundi – Samedi : 9h à 19h" />
              </div>
              <div className="border-t border-clay pt-4">
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">Paiements mobiles (affichés au client à la commande)</p>
                <div className="space-y-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-bark/60">Numéro Wave</label>
                    <input data-testid="admin-shop-wave-input" value={shopForm.wave_number || ""} onChange={(e) => setShopForm((f) => ({ ...f, wave_number: e.target.value }))} className={inputClass} placeholder="+221 77 123 45 67" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-bark/60">Numéro Orange Money</label>
                    <input data-testid="admin-shop-om-input" value={shopForm.orange_money_number || ""} onChange={(e) => setShopForm((f) => ({ ...f, orange_money_number: e.target.value }))} className={inputClass} placeholder="+221 77 123 45 67" />
                  </div>
                  <p className="text-xs text-bark/40">Ces numéros sont aussi modifiables dans l'onglet « Paiements ».</p>
                </div>
              </div>
              <div className="border-t border-clay pt-4">
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">Réseaux sociaux (affichés dans le pied de page)</p>
                <div className="space-y-3">
                  <input data-testid="admin-shop-instagram-input" value={shopForm.instagram || ""} onChange={(e) => setShopForm((f) => ({ ...f, instagram: e.target.value }))} className={inputClass} placeholder="Instagram — https://instagram.com/samakeur" />
                  <input data-testid="admin-shop-facebook-input" value={shopForm.facebook || ""} onChange={(e) => setShopForm((f) => ({ ...f, facebook: e.target.value }))} className={inputClass} placeholder="Facebook — https://facebook.com/samakeur" />
                  <input data-testid="admin-shop-tiktok-input" value={shopForm.tiktok || ""} onChange={(e) => setShopForm((f) => ({ ...f, tiktok: e.target.value }))} className={inputClass} placeholder="TikTok — https://tiktok.com/@samakeur" />
                  <p className="text-xs text-bark/40">Laissez vide pour masquer l'icône correspondante.</p>
                </div>
              </div>
              <button
                data-testid="admin-shop-save"
                onClick={saveShop}
                disabled={savingShop}
                className="flex h-12 items-center gap-2 rounded-full bg-terracotta px-8 text-xs font-bold uppercase tracking-[0.15em] text-sand transition-colors hover:bg-terracotta-dark disabled:opacity-50"
              >
                {savingShop && <Loader2 className="h-4 w-4 animate-spin" />}
                Enregistrer les paramètres
              </button>
            </div>

            <div className="mt-10 max-w-2xl">
              <AdminAccount user={user} onEmailChanged={(newEmail) => setUser((u) => ({ ...u, email: newEmail }))} />
            </div>
          </section>
        )}
      </main>

      {previewItem && (
        <div
          data-testid="order-product-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-bark/50 p-6 backdrop-blur-sm"
          onClick={() => setPreviewItem(null)}
        >
          <div className="w-full max-w-lg border border-clay bg-cream" onClick={(e) => e.stopPropagation()}>
            {previewItem.product ? (
              <div className="flex flex-col sm:flex-row">
                <div className="aspect-[4/5] w-full shrink-0 overflow-hidden bg-white sm:w-48">
                  <img
                    src={previewItem.product.image}
                    alt={previewItem.product.name}
                    data-testid="order-product-modal-image"
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="flex-1 p-6">
                  <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-terracotta" data-testid="order-product-modal-category">
                    {previewItem.product.category}
                  </p>
                  <h3 className="mt-2 font-display text-2xl font-semibold text-bark" data-testid="order-product-modal-name">
                    {previewItem.product.name}
                  </h3>
                  <p className="mt-1 text-lg font-bold text-terracotta" data-testid="order-product-modal-price">
                    {fcfa(previewItem.product.price)}
                  </p>
                  <p className="mt-1 text-[11px] tracking-widest text-bark/40" data-testid="order-product-modal-ref">
                    Réf. : {previewItem.product.id}
                  </p>
                  <p className="mt-4 max-h-40 overflow-y-auto text-sm leading-relaxed text-bark/60" data-testid="order-product-modal-description">
                    {previewItem.product.description || "Pas de description."}
                  </p>
                  <p className="mt-3 border-t border-clay pt-3 text-xs text-bark/50">
                    Commandé : ×{previewItem.item.quantity}
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-8">
                <h3 className="font-display text-2xl font-semibold text-bark">{previewItem.item.name}</h3>
                <p className="mt-2 text-sm text-bark/50" data-testid="order-product-modal-deleted">Ce produit n'est plus dans le catalogue.</p>
                <p className="mt-1 text-xs text-bark/40">Commandé : ×{previewItem.item.quantity} — {fcfa(previewItem.item.price)} / unité</p>
              </div>
            )}
            <div className="border-t border-clay p-4 text-right">
              <button
                data-testid="order-product-modal-close"
                onClick={() => setPreviewItem(null)}
                className="h-10 rounded-full border border-clay px-6 text-xs font-bold uppercase tracking-[0.15em] text-bark/60 transition-colors hover:border-terracotta hover:text-terracotta"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteOrder && (
        <div
          data-testid="delete-order-confirm-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-bark/50 p-6 backdrop-blur-sm"
          onClick={() => setConfirmDeleteOrder(null)}
        >
          <div className="w-full max-w-md border border-clay bg-cream p-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl font-semibold text-bark">Supprimer cette commande ?</h3>
            <p className="mt-3 text-sm leading-relaxed text-bark/60">
              La commande {confirmDeleteOrder.order_number} ({confirmDeleteOrder.customer.name} — {fcfa(confirmDeleteOrder.total)}) sera
              supprimée définitivement. Cette action est irréversible.
            </p>
            <div className="mt-8 flex gap-3">
              <button
                data-testid="delete-order-confirm-yes"
                onClick={() => deleteOrder(confirmDeleteOrder)}
                className="flex h-11 flex-1 items-center justify-center rounded-full bg-destructive text-xs font-bold uppercase tracking-[0.15em] text-sand transition-opacity hover:opacity-90"
              >
                Supprimer
              </button>
              <button
                data-testid="delete-order-confirm-no"
                onClick={() => setConfirmDeleteOrder(null)}
                className="flex h-11 flex-1 items-center justify-center rounded-full border border-clay text-xs font-bold uppercase tracking-[0.15em] text-bark/60 transition-colors hover:border-terracotta hover:text-terracotta"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {renameCat && (
        <div
          data-testid="rename-cat-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-bark/50 p-6 backdrop-blur-sm"
          onClick={() => setRenameCat(null)}
        >
          <form className="w-full max-w-md border border-clay bg-cream p-8" onClick={(e) => e.stopPropagation()} onSubmit={renameCategory}>
            <h3 className="font-display text-2xl font-semibold text-bark">Renommer la catégorie</h3>
            <p className="mt-3 text-sm leading-relaxed text-bark/60">
              Tous les produits de « {renameCat} » seront automatiquement déplacés vers le nouveau nom.
            </p>
            <input
              data-testid="rename-cat-input"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              autoFocus
              className={`${inputClass} mt-6`}
              placeholder="Nouveau nom"
            />
            <div className="mt-6 flex gap-3">
              <button
                type="submit"
                disabled={savingRename || !renameValue.trim()}
                data-testid="rename-cat-confirm"
                className="flex h-11 flex-1 items-center justify-center gap-2 rounded-full bg-terracotta text-xs font-bold uppercase tracking-[0.15em] text-sand transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {savingRename && <Loader2 className="h-4 w-4 animate-spin" />}
                Renommer
              </button>
              <button
                type="button"
                data-testid="rename-cat-cancel"
                onClick={() => setRenameCat(null)}
                className="flex h-11 flex-1 items-center justify-center rounded-full border border-clay text-xs font-bold uppercase tracking-[0.15em] text-bark/60 transition-colors hover:border-terracotta hover:text-terracotta"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {confirmDeleteCat && (
        <div
          data-testid="delete-cat-confirm-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-bark/50 p-6 backdrop-blur-sm"
          onClick={() => setConfirmDeleteCat(null)}
        >
          <div className="w-full max-w-md border border-clay bg-cream p-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl font-semibold text-bark">Supprimer cette catégorie ?</h3>
            <p className="mt-3 text-sm leading-relaxed text-bark/60">
              La catégorie « {confirmDeleteCat} » ne contient aucun produit. Elle sera supprimée définitivement de la
              boutique. Cette action est irréversible.
            </p>
            <div className="mt-8 flex gap-3">
              <button
                data-testid="delete-cat-confirm-yes"
                onClick={() => deleteCategory(confirmDeleteCat)}
                className="flex h-11 flex-1 items-center justify-center rounded-full bg-destructive text-xs font-bold uppercase tracking-[0.15em] text-sand transition-opacity hover:opacity-90"
              >
                Supprimer
              </button>
              <button
                data-testid="delete-cat-confirm-no"
                onClick={() => setConfirmDeleteCat(null)}
                className="flex h-11 flex-1 items-center justify-center rounded-full border border-clay text-xs font-bold uppercase tracking-[0.15em] text-bark/60 transition-colors hover:border-terracotta hover:text-terracotta"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div
          data-testid="delete-confirm-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-bark/50 p-6 backdrop-blur-sm"
          onClick={() => setConfirmDelete(null)}
        >
          <div className="w-full max-w-md border border-clay bg-cream p-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl font-semibold text-bark">Supprimer définitivement ?</h3>
            <p className="mt-3 text-sm leading-relaxed text-bark/60">
              « {confirmDelete.name} » sera supprimé définitivement de la base de données : il n'apparaîtra plus ni dans
              l'admin ni sur la boutique. Cette action est irréversible.
            </p>
            <div className="mt-8 flex gap-3">
              <button
                data-testid="delete-confirm-yes"
                onClick={() => deleteProduct(confirmDelete.id)}
                className="flex h-11 flex-1 items-center justify-center rounded-full bg-destructive text-xs font-bold uppercase tracking-[0.15em] text-sand transition-opacity hover:opacity-90"
              >
                Supprimer définitivement
              </button>
              <button
                data-testid="delete-confirm-no"
                onClick={() => setConfirmDelete(null)}
                className="flex h-11 flex-1 items-center justify-center rounded-full border border-clay text-xs font-bold uppercase tracking-[0.15em] text-bark/60 transition-colors hover:border-terracotta hover:text-terracotta"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
